import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import {
  SELECT_CLASS,
  textareaClass,
} from "@/features/data-curation/components/SchemaColumnsEditor";
import {
  EMPTY_SCOPE,
  MAX_MONITOR_DIRECTIONS,
  WEEKDAYS,
  WHOLE_DATASET,
  defaultMonitorSchedule,
  isMonitorTime,
  monitorRequestMessage,
  monitorScheduleText,
  monitorScopeIsEmpty,
  monitorScopeItems,
  withScopeColumn,
  withoutScopeItem,
  type MonitorSchedule,
  type MonitorScope,
} from "@/features/data-curation/monitors";
import type { CurationColumn, DatasetMonitorCadence } from "@/features/data-curation/types";
import { cn } from "@/utils/cn";
import { CalendarClock, X } from "lucide-react";
import { useId, useState, type FormEvent } from "react";

const LABEL_CLASS = "block text-xs font-medium text-zinc-600";
const CHECKBOX_CLASS =
  "mt-0.5 h-4 w-4 shrink-0 rounded-none border-zinc-400 text-zinc-900 focus:ring-2 focus:ring-zinc-900";
const RADIO_CLASS =
  "h-4 w-4 shrink-0 border-zinc-400 text-zinc-900 focus:ring-2 focus:ring-zinc-900";

const CADENCES: { value: DatasetMonitorCadence; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const MONTH_DAYS = Array.from({ length: 28 }, (_, index) => index + 1);

interface MonitorFormProps {
  datasetId: string;
  /** The dataset's columns, in grid order. */
  columns: Pick<CurationColumn, "column_id" | "label">[];
  /** The selection when the dialog opened: a first choice of the values to update. */
  initialScope: MonitorScope | null;
  agentWorking: boolean;
  onClose: () => void;
  /** Puts the request in the dataset's chat, as Retry does. */
  onSend: (message: string) => void;
}

/** The dialog's fields. The dialog mounts them each time it opens, so they start fresh. */
const MonitorForm = ({
  datasetId,
  columns,
  initialScope,
  agentWorking,
  onClose,
  onSend,
}: MonitorFormProps) => {
  const id = useId();
  const someSelected = initialScope != null && !initialScope.all;
  const [addRows, setAddRows] = useState(!someSelected);
  const [update, setUpdate] = useState(initialScope != null);
  const [target, setTarget] = useState<"all" | "some">(someSelected ? "some" : "all");
  const [scope, setScope] = useState<MonitorScope>(() =>
    initialScope && !initialScope.all ? initialScope : EMPTY_SCOPE,
  );
  const [directions, setDirections] = useState("");
  const [schedule, setSchedule] = useState<MonitorSchedule>(() => defaultMonitorSchedule());
  const [showProblems, setShowProblems] = useState(false);

  const items = monitorScopeItems(scope);
  const addable = columns.filter(
    (column) => !scope.columns.some((item) => item.columnId === column.column_id),
  );
  const scopeEmpty = target === "some" && monitorScopeIsEmpty(scope);
  const workProblem =
    showProblems && !addRows && !update ? "Choose what each run does." : null;
  const scopeProblem =
    showProblems && update && scopeEmpty ? "Add a column, or choose all values." : null;
  const timeProblem =
    showProblems && !isMonitorTime(schedule.time) ? "Give a time, such as 09:00." : null;

  const change = (patch: Partial<MonitorSchedule>) =>
    setSchedule((current) => ({ ...current, ...patch }));
  const addColumn = (columnId: string) => {
    const column = columns.find((item) => item.column_id === columnId);
    if (!column) return;
    setScope((current) =>
      withScopeColumn(current, { columnId: column.column_id, columnLabel: column.label }),
    );
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowProblems(true);
    if ((!addRows && !update) || (update && scopeEmpty) || !isMonitorTime(schedule.time)) {
      return;
    }
    onSend(
      monitorRequestMessage({
        datasetId,
        addRows,
        update: update ? (target === "all" ? WHOLE_DATASET : scope) : null,
        schedule,
        directions,
      }),
    );
    onClose();
  };

  return (
    <form noValidate onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>Set up a monitor</DialogTitle>
        <DialogDescription>
          A monitor keeps this dataset up to date. On the schedule that you choose, the
          agent adds new rows, updates values, or both. A checkpoint is saved before each
          run, so you can undo a run.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <fieldset aria-describedby={workProblem ? `${id}-work-error` : undefined}>
          <legend className={LABEL_CLASS}>What each run does</legend>
          <div className="mt-1.5 space-y-2.5">
            <label className="flex items-start gap-2 text-sm text-zinc-800">
              <input
                type="checkbox"
                checked={addRows}
                onChange={(event) => setAddRows(event.target.checked)}
                className={CHECKBOX_CLASS}
              />
              <span>
                Add new rows
                <span className="block text-xs text-zinc-500">
                  Look for new sources that fit this dataset, and add their rows.
                </span>
              </span>
            </label>
            <div>
              <label className="flex items-start gap-2 text-sm text-zinc-800">
                <input
                  type="checkbox"
                  checked={update}
                  onChange={(event) => setUpdate(event.target.checked)}
                  className={CHECKBOX_CLASS}
                />
                <span>
                  Update values
                  <span className="block text-xs text-zinc-500">
                    Check values, and change the ones that are out of date.
                  </span>
                </span>
              </label>
              {update && (
                <fieldset className="ml-6 mt-2 space-y-2">
                  <legend className="sr-only">Which values</legend>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <label className="flex items-center gap-1.5 text-sm text-zinc-700">
                      <input
                        type="radio"
                        name={`${id}-target`}
                        checked={target === "all"}
                        onChange={() => setTarget("all")}
                        className={RADIO_CLASS}
                      />
                      All values
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-zinc-700">
                      <input
                        type="radio"
                        name={`${id}-target`}
                        checked={target === "some"}
                        onChange={() => setTarget("some")}
                        className={RADIO_CLASS}
                      />
                      Only these
                    </label>
                  </div>
                  {target === "some" && (
                    <div className="space-y-2">
                      {items.length > 0 && (
                        <ul aria-label="Values to update" className="flex flex-wrap gap-1.5">
                          {items.map((item) => (
                            <li
                              key={item.key}
                              className="inline-flex items-center rounded-[3px] border border-amber-300 bg-amber-50 text-xs text-amber-900"
                            >
                              <span className="py-0.5 pl-2">{item.label}</span>
                              <button
                                type="button"
                                aria-label={`Remove ${item.label}`}
                                title="Remove"
                                onClick={() =>
                                  setScope((current) => withoutScopeItem(current, item))
                                }
                                className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded-[3px] hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
                              >
                                <X className="h-3 w-3" aria-hidden />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {addable.length > 0 && (
                        <select
                          aria-label="Add a column"
                          value=""
                          onChange={(event) => addColumn(event.target.value)}
                          className={cn(SELECT_CLASS, "block max-w-full")}
                        >
                          <option value="">Add a column…</option>
                          {addable.map((column) => (
                            <option key={column.column_id} value={column.column_id}>
                              {column.label}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-zinc-500">
                        To add rows or single cells, select them in the grid before you open
                        this.
                      </p>
                      {scopeProblem && (
                        <p className="text-xs text-red-700">{scopeProblem}</p>
                      )}
                    </div>
                  )}
                </fieldset>
              )}
            </div>
          </div>
          {workProblem && (
            <p id={`${id}-work-error`} className="mt-1 text-xs text-red-700">
              {workProblem}
            </p>
          )}
        </fieldset>
        <div>
          <label htmlFor={`${id}-directions`} className={LABEL_CLASS}>
            Directions <span className="font-normal text-zinc-500">(optional)</span>
          </label>
          <textarea
            id={`${id}-directions`}
            value={directions}
            rows={3}
            maxLength={MAX_MONITOR_DIRECTIONS}
            placeholder="For example: only phase 3 trials. When a trial ends, change its status."
            onChange={(event) => setDirections(event.target.value)}
            className={textareaClass()}
          />
        </div>
        <fieldset>
          <legend className={LABEL_CLASS}>How often</legend>
          <div className="mt-1 inline-flex overflow-hidden rounded-[3px] border border-zinc-300">
            {CADENCES.map((cadence) => (
              <label
                key={cadence.value}
                className={cn(
                  "cursor-pointer border-r border-zinc-300 px-3 py-1 text-sm last:border-r-0 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-zinc-900",
                  schedule.cadence === cadence.value
                    ? "bg-zinc-900 text-white"
                    : "bg-white text-zinc-700 hover:bg-zinc-100",
                )}
              >
                <input
                  type="radio"
                  name={`${id}-cadence`}
                  value={cadence.value}
                  checked={schedule.cadence === cadence.value}
                  onChange={() => change({ cadence: cadence.value })}
                  className="sr-only"
                />
                {cadence.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-wrap items-start gap-3">
          {schedule.cadence === "weekly" && (
            <label className={LABEL_CLASS}>
              Day
              <select
                value={schedule.weekday}
                onChange={(event) => change({ weekday: Number(event.target.value) })}
                className={cn(SELECT_CLASS, "mt-1 block")}
              >
                {WEEKDAYS.map((day, index) => (
                  <option key={day} value={index}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
          )}
          {schedule.cadence === "monthly" && (
            <label className={LABEL_CLASS}>
              Day of the month
              <select
                value={schedule.monthDay}
                onChange={(event) => change({ monthDay: Number(event.target.value) })}
                className={cn(SELECT_CLASS, "mt-1 block")}
              >
                {MONTH_DAYS.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div>
            <label htmlFor={`${id}-time`} className={LABEL_CLASS}>
              Time
            </label>
            <Input
              id={`${id}-time`}
              type="time"
              step={60}
              value={schedule.time}
              error={Boolean(timeProblem)}
              aria-describedby={timeProblem ? `${id}-time-error` : undefined}
              onChange={(event) => change({ time: event.target.value })}
              className="mt-1 w-32 text-sm"
            />
            {timeProblem && (
              <p id={`${id}-time-error`} className="mt-1 text-xs text-red-700">
                {timeProblem}
              </p>
            )}
          </div>
        </div>
        <p className="text-xs text-zinc-500" aria-live="polite">
          Runs {monitorScheduleText(schedule)}, in your time zone ({schedule.timeZone}).
          {schedule.cadence === "monthly" &&
            " Days 29 to 31 are left out, so that it runs every month."}
        </p>
        {agentWorking && (
          <p className="text-xs text-zinc-500">
            The agent is busy. The request waits in the chat box until you send it.
          </p>
        )}
      </div>
      <DialogFooter className="mt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit">
          <CalendarClock className="h-4 w-4" aria-hidden />
          Create monitor
        </Button>
      </DialogFooter>
    </form>
  );
};

interface MonitorDialogProps extends Omit<MonitorFormProps, "initialScope"> {
  open: boolean;
  /** The selection when the dialog opened; null when it opened without one. */
  scope: MonitorScope | null;
}

/** Asks the chat's agent to create a monitor: new rows, updated values, or both, on a schedule. */
export const MonitorDialog = ({ open, scope, onClose, ...props }: MonitorDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      if (!next) onClose();
    }}
  >
    <DialogContent className="max-h-[90vh] overflow-y-auto">
      <MonitorForm {...props} initialScope={scope} onClose={onClose} />
    </DialogContent>
  </Dialog>
);
